<template>
  <v-container fluid>
    <v-toolbar color="secondary" flat dense>
      <v-toolbar-title>Combined Stock Value Report</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn small text to="/stock-reports">
        <v-icon left dark>mdi-keyboard-backspace</v-icon>Back
      </v-btn>
    </v-toolbar>
    <v-card>
      <v-card-text>
        <v-row>
          <v-col cols="12" xs="12" md="3">
            <v-autocomplete
              name="categoryFilter"
              dense
              outlined
              v-model="categoryId"
              :items="categories"
              label="Select Category (Optional)"
              item-text="Name"
              item-value="id"
              clearable
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" xs="12" md="3">
            <v-autocomplete
              name="brandFilter"
              dense
              outlined
              v-model="brandId"
              :items="brands"
              label="Select Brand (Optional)"
              item-text="Name"
              item-value="id"
              clearable
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" xs="12" md="3">
            <v-autocomplete
              name="storeFilter"
              dense
              outlined
              v-model="storeId"
              :items="stores"
              label="Select Shop (Optional)"
              item-text="Name"
              item-value="id"
              clearable
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" xs="12" md="3">
            <v-btn class="primary" :loading="loader" @click="queryData">Generate Report</v-btn>
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <div v-if="reportData.length > 0">
          <v-row dense class="subtitle-2 text--primary mt-4">
            <v-col cols="6">
              <span>Total Stock Value (Buying Price): {{ stockValueBuying.toLocaleString() }}</span>
              <br />
              <span>Total Stock Value (Selling Price): {{ stockValueSelling.toLocaleString() }}</span>
            </v-col>

            <v-col cols="6">
              <v-btn class="float-right" text @click="generatePdfReport" color="accent">
                <v-icon color="black" left>mdi-file-pdf-box</v-icon> Print
                Pdf Report
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense class="subtitle-2 text--primary">
            <v-col cols="12">
              <v-text-field v-model="search" label="Search" class="mb-4"></v-text-field>
              <v-data-table 
                :loading="loader" 
                loading-text="Loading .... please wait" 
                dense 
                :headers="headers"
                :items="reportData" 
                :search="search" 
                :items-per-page="2000"
                class="font-weight-medium"
              >
                <template v-slot:item.BuyingPrice="{ item }">
                  <span>{{ item.BuyingPrice | currency }}</span>
                </template>

                <template v-slot:item.SellingPrice="{ item }">
                  <span>{{ item.SellingPrice | currency }}</span>
                </template>

                <template v-slot:item.TotalRowBuyingPrice="{ item }">
                  <span>{{ item.TotalRowBuyingPrice | currency }}</span>
                </template>

                <template v-slot:item.TotalRowSellingPrice="{ item }">
                  <span>{{ item.TotalRowSellingPrice | currency }}</span>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </div>

        <div v-else-if="!loader">
          <v-alert type="info" class="mt-4">
            <v-icon left>mdi-information</v-icon>
            Click "Generate Report" to load combined stock value data. Optionally select a category, brand, or shop to filter results.
          </v-alert>
        </div>
      </v-card-text>
    </v-card>

    <!-- pdf preview -->
    <pdf-preview ref="pdfPreview"></pdf-preview>
    <!-- end -->

    <!-- snackbar -->
    <snackbar ref="snackbar"></snackbar>
  </v-container>
</template>

<script>
import { http, host } from "@/common/index";

export default {
  data() {
    return {
      loader: false,
      search: null,
      categoryId: null,
      brandId: null,
      storeId: null,
      categories: [],
      brands: [],
      stores: [],
      reportData: [],
      stockValueBuying: 0,
      stockValueSelling: 0,
      headers: [
        { text: "Product", value: "Product" },
        { text: "Available Qty", value: "AvailableStock" },
        { text: "Inventory Unit", value: "BaseUnit" },
        { text: "Buying Price", value: "BuyingPrice" },
        { text: "Selling Price", value: "SellingPrice" },
        { text: "Row Total (buying)", value: "TotalRowBuyingPrice" },
        { text: "Row Total (Selling)", value: "TotalRowSellingPrice" }
      ]
    };
  },
  methods: {
    queryData() {
      const self = this;
      self.loader = true;
      
      // Build URL with optional filters
      let url = "/combined-stock-value";
      let params = [];
      if (self.categoryId) params.push(`categoryId=${self.categoryId}`);
      if (self.brandId) params.push(`brandId=${self.brandId}`);
      if (self.storeId) params.push(`storeId=${self.storeId}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      this.$store
        .dispatch("get", url)
        .then(res => {
          if (res.ResultCode == 1200) {
            self.reportData = res.ResponseData.StockData || [];
            self.stockValueBuying = res.ResponseData.TotalBuyingPriceValue || 0;
            self.stockValueSelling = res.ResponseData.TotalSellingPriceValue || 0;
          } else {
            this.$refs.snackbar.show(res.ResultDesc || res.msg, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        })
        .finally(() => {
          self.loader = false;
        });
    },
    generatePdfReport() {
      let url = "/print-combined-stock-value-report";
      let params = [];
      if (this.categoryId) params.push(`categoryId=${this.categoryId}`);
      if (this.brandId) params.push(`brandId=${this.brandId}`);
      if (this.storeId) params.push(`storeId=${this.storeId}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const apiUrl = `${host}${url}`;

      const self = this;

      http.get(apiUrl, { responseType: "blob" }).then(response => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        var fileUrl = URL.createObjectURL(blob);
        self.$refs.pdfPreview.show(fileUrl);
      }).catch(() => {
        this.$refs.snackbar.show("PDF generation failed", "red");
      });
    },
    getBrands() {
      const self = this;
      this.$store
        .dispatch("get", `/brands`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.brands = res.ResponseData || [];
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    getStores() {
      const self = this;
      this.$store
        .dispatch("get", `/stores`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.stores = res.ResponseData || [];
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    getCategories() {
      const self = this;
      this.$store
        .dispatch("get", `/products-categories`)
        .then((res) => {
          if (res.ResultCode == 1200) {
            self.categories = res.ResponseData || [];
          } else {
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch((err) => {
          this.$refs.snackbar.show(err, "red");
        });
    }
  },
  created() {
    this.getCategories();
    this.getBrands();
    this.getStores();
  }
};
</script>

<style lang="scss" scoped></style>